import React, { useState, useEffect } from 'react';
import {
    FaGithub, FaCheck, FaTimes, FaExclamationTriangle,
    FaSearch, FaFilter, FaLaptopCode, FaUserPlus, FaCamera, FaSave, FaBug, FaCode, FaUserShield, FaSync, FaBell, FaTrash,
    FaFileExport, FaStickyNote, FaFileUpload
} from 'react-icons/fa';
import ManageAccessModal from './ManageAccessModal';
import { db, storage } from '../../../firebase';
import { collection, onSnapshot, doc, updateDoc, setDoc, getDocs, deleteDoc, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useEmployeeAuth } from '../../auth/EmployeeAuthContext';

const FE_STATUS_OPTIONS = [
    { value: 'v1-red', label: 'Version 1 (Default)', color: 'bg-red-500/20 text-red-500 border-red-500/50', icon: '🔴' },
    { value: 'in-progress', label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50', icon: '🟡' },
    { value: 'completed-v2', label: 'Completed V2', color: 'bg-green-500/20 text-green-500 border-green-500/50', icon: '🟢' },
];

const BE_STATUS_OPTIONS = [
    { value: 'not-started', label: 'Not Started', color: 'bg-red-500/20 text-red-500 border-red-500/50', icon: '🔴' },
    { value: 'in-progress', label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50', icon: '🟡' },
    { value: 'completed', label: 'Completed', color: 'bg-green-500/20 text-green-500 border-green-500/50', icon: '🟢' },
];

const TechTrackingTable = ({ appId, config: propConfig }) => {
    const { user } = useEmployeeAuth();
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');
    const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [syncRequest, setSyncRequest] = useState(null);

    // Dynamic Configuration
    const config = propConfig || {
        title: appId === 'customer-app' ? 'Customer App' : 'Partner App',
        owner: 'Driveeasy-Wheelpro',
        repo: appId === 'customer-app' ? 'driveeasy-app-frontend' : 'driveeasy-partnerapp-frontend',
        branch: 'abhishek-dev',
        screensPath: 'src/screens'
    };
    const title = config.title;

    // Firestore Collection Reference
    const collectionName = `tech_${appId.replace(/-/g, '_')}_screens`;
    const screensCollectionRef = collection(db, collectionName);

    // LISTEN FOR SYNC REQUESTS
    useEffect(() => {
        const reqRef = doc(db, 'tech_sync_requests', appId);
        const unsub = onSnapshot(reqRef, (doc) => {
            setSyncRequest(doc.exists() ? doc.data() : null);
        });
        return () => unsub();
    }, [appId]);

    // HANDLE SYNC REQUEST
    const handleRequestSync = async () => {
        try {
            await setDoc(doc(db, 'tech_sync_requests', appId), {
                requestedBy: user?.email,
                requestedByName: user?.name || user?.email,
                requestedAt: new Date(),
                status: 'pending'
            });
            alert("Sync request sent to HR!");
        } catch (error) {
            console.error(error);
            alert("Failed to send request.");
        }
    };

    // Real-time Listener
    useEffect(() => {
        const unsubscribe = onSnapshot(screensCollectionRef, (snapshot) => {
            const fetchedData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setData(fetchedData);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [collectionName]);

    // Update Handler
    const handleUpdate = async (id, field, value) => {
        const docRef = doc(db, collectionName, id);
        await updateDoc(docRef, {
            [field]: value,
            lastEditedBy: {
                name: user?.name || 'Unknown',
                photo: user?.photoURL || null, // Assuming user object has photoURL or just use letter avatar
                email: user?.email
            },
            updatedAt: new Date()
        });
    };

    const handleDeleteRow = async (id, screenName) => {
        if (!window.confirm(`Are you sure you want to delete "${screenName}"? This cannot be undone.`)) return;
        try {
            await deleteDoc(doc(db, collectionName, id));
        } catch (error) {
            console.error(error);
            alert("Failed to delete row.");
        }
    };

    // GitHub Sync Handler
    const handleGitHubSync = async (isReset = false) => {
        const confirmMsg = isReset
            ? "WARNING: THIS WILL DELETE ALL DATA (Statuses, Test Cases, Blockers). Proceed with Reset & Resync?"
            : "This will scan GitHub for new screens and remove deleted ones. Proceed?";

        if (!window.confirm(confirmMsg)) return;
        setLoading(true);

        try {
            // 1. Get Repo Details (Use Registry Config)
            const owner = config.owner;
            const repo = config.repo;
            const branch = config.branch;
            const screensPath = config.screensPath || 'src/screens';

            // 2. Get Token
            let token = localStorage.getItem('gh_token');
            if (!token) {
                token = prompt(`Enter GitHub Token to sync with ${owner}/${repo} (${branch}):`);
                if (!token) {
                    setLoading(false);
                    return;
                }
                localStorage.setItem('gh_token', token);
            }

            // --- RESET LOGIC ---
            if (isReset) {
                console.log("Nuclear Reset: Clearing database...");
                const batch = writeBatch(db);
                const snapshot = await getDocs(screensCollectionRef);
                snapshot.docs.forEach((doc) => batch.delete(doc.ref));
                await batch.commit();
                // We'll let the onSnapshot listener update the local 'data' state, 
                // but for the diff logic below, we treat everything as new.
            }

            // 3. Fetch Tree
            console.log(`Fetching from https://api.github.com/repos/${owner}/${repo}/git/trees/${branch}?recursive=1`);
            const response = await fetch(`https://api.github.com/repos/${owner}/${repo}/git/trees/${branch}?recursive=1`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('gh_token');
                    throw new Error("Invalid GitHub Token. Please try again.");
                }
                throw new Error(`GitHub API Error: ${response.statusText}`);
            }

            const treeData = await response.json();
            if (!treeData.tree) throw new Error("Failed to parse repository tree.");

            // 4. Filter Screens (Search for files in 'screens' or 'pages' folders, or ending in 'Screen.tsx')
            const screenFiles = treeData.tree.filter(f => {
                const isTsx = f.path.endsWith('.tsx');
                const isBlob = f.type === 'blob';
                const isInScreenFolder = f.path.toLowerCase().includes(`${screensPath.toLowerCase()}/`) || f.path.toLowerCase().includes('/pages/');
                const isScreenFile = f.path.endsWith('Screen.tsx');

                // Exclude common non-screen files
                const isExcluded = f.path.endsWith('index.tsx') || f.path.endsWith('styles.tsx') || f.path.endsWith('.test.tsx');

                return isTsx && isBlob && (isInScreenFolder || isScreenFile) && !isExcluded;
            });

            // 5. Diff & Sync
            // If reset, we treat currentIds as empty so everything is added fresh
            const currentIds = isReset ? new Set() : new Set(data.map(d => d.id));
            let added = 0;
            let deleted = 0;

            // --- PROCESS ADDS ---
            for (const file of screenFiles) {
                const pathParts = file.path.split('/');
                const fileName = pathParts[pathParts.length - 1].replace('.tsx', '');

                // Smart Category Detection: Traverse upwards to find the feature/domain name
                const skipFolders = ['screens', 'pages', 'components', 'context', 'store', 'hooks', 'utils', 'services', 'api', 'data', 'navigation', 'assets', 'styles', 'src', 'features'];
                let categoryName = 'General';

                for (let i = pathParts.length - 2; i >= 0; i--) {
                    const folderName = pathParts[i].toLowerCase();
                    if (!skipFolders.includes(folderName)) {
                        categoryName = pathParts[i];
                        break;
                    }
                }
                const category = categoryName.charAt(0).toUpperCase() + categoryName.slice(1);

                // Convert camelCase/PascalCase to Spaced Title: 'LoginScreen' -> 'Login Screen', 'Step1Arrival' -> 'Step 1 Arrival'
                const screenName = fileName
                    .replace(/([A-Z])/g, ' $1') // Add space before caps
                    .replace(/([0-9]+)/g, ' $1 ') // Add spaces around numbers
                    .replace(/\s+/g, ' ') // Collapse multiple spaces
                    .trim();

                const docId = screenName.toLowerCase().replace(/[^a-z0-9]/g, '_');

                const link = `https://github.com/${owner}/${repo}/blob/${branch}/${file.path}`;

                if (!currentIds.has(docId)) {
                    await setDoc(doc(db, collectionName, docId), {
                        screen: screenName,
                        category: category,
                        feStatus: 'v1-red',
                        beStatus: 'not-started',
                        apiReady: false,
                        testCasesCount: 0,
                        testCasesDone: false,
                        githubLink: link,
                        createdAt: new Date(),
                        updatedAt: new Date()
                    });
                    added++;
                }
            }

            // --- PROCESS DELETES (Only if not in reset mode) ---
            if (!isReset) {
                const validRemoteLinks = new Set(screenFiles.map(f => `https://github.com/${owner}/${repo}/blob/${branch}/${f.path}`));
                for (const item of data) {
                    if (item.githubLink && item.githubLink.includes(`github.com/${owner}/${repo}`)) {
                        if (!validRemoteLinks.has(item.githubLink)) {
                            await deleteDoc(doc(db, collectionName, item.id));
                            deleted++;
                        }
                    }
                }
            }

            // 6. Access Granted! Clear the Request
            await deleteDoc(doc(db, 'tech_sync_requests', appId));

            alert(`${isReset ? 'Reset & ' : ''}Sync Successful! 🚀\n\nFound: ${screenFiles.length} screens\nAdded/Restored: ${added}\nDeleted: ${deleted}`);

        } catch (error) {
            console.error(error);
            alert("Sync Failed: " + error.message);
        }
        setLoading(false);
    };

    const handleImageUpload = async (id, file) => {
        if (!file) return;
        try {
            const storageRef = ref(storage, `tech_screenshots/${appId}/${id}_${Date.now()}`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            handleUpdate(id, 'screenshotUrl', url);
        } catch (error) {
            console.error("Upload failed", error);
            alert("Failed to upload screenshot");
        }
    };

    const handleBeFileUpload = async (id, file) => {
        if (!file) return;
        try {
            const storageRef = ref(storage, `tech_be_assets/${appId}/${id}_${file.name}`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            handleUpdate(id, 'beFile', { url, name: file.name, uploadedAt: new Date() });
        } catch (error) {
            console.error("BE File Upload failed", error);
            alert("Failed to upload backend file");
        }
    };


    // Calculate Stats
    const total = data.length;
    const feDone = data.filter(item => item.feStatus === 'completed-v2').length;
    const beDone = data.filter(item => item.beStatus === 'completed').length;
    // Let's define "Launch Ready" as both FE and BE done + API Ready + Test Cases Done ? 
    // Or just FE V2 & BE Completed? Let's use FE V2 + BE Completed for progress bar
    const fullyDone = data.filter(item => item.feStatus === 'completed-v2' && item.beStatus === 'completed').length;
    const progressPercent = total > 0 ? Math.round((fullyDone / total) * 100) : 0;

    // Get unique categories
    const categories = ['All', ...new Set(data.map(item => item.category).filter(Boolean))];

    const filteredData = data.filter(item => {
        const matchesSearch = item.screen.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'All' || item.category === filterCategory;
        return matchesSearch && matchesCategory;
    });

    // Access Check
    const hasAccess = user?.role === 'hr' || user?.techAccess?.includes(appId);

    if (!hasAccess && !loading) {
        return (
            <div className="min-h-screen bg-neutral-900 text-gray-100 flex flex-col items-center justify-center p-6 text-center">
                <div className="w-24 h-24 bg-red-500/10 rounded-full flex items-center justify-center mb-6">
                    <FaUserShield className="text-5xl text-red-500" />
                </div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent mb-2">
                    Access Denied
                </h1>
                <p className="text-gray-400 max-w-md">
                    You do not have permission to view the <span className="text-white font-semibold">{title}</span>.
                    Please contact HR to request access.
                </p>
            </div>
        );
    }

    if (loading) return <div className="p-10 text-white">Loading Tracker data...</div>;

    return (
        <div className="min-h-screen bg-neutral-900 text-gray-100 p-6 sm:p-10 font-poppins">

            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                        {title}
                    </h1>
                    <p className="text-gray-400 text-sm mt-1 flex items-center gap-2">
                        <FaLaptopCode /> Tracking development velocity & readiness
                    </p>
                </div>

                <div className="flex items-center gap-6">
                    {/* Sync Workflow Button */}
                    {user?.role === 'hr' ? (
                        <div className="flex items-center gap-3">
                            {syncRequest && (
                                <div className="flex items-center gap-1.5 animate-pulse text-yellow-500 font-semibold text-xs border border-yellow-500/30 bg-yellow-500/10 px-2 py-1 rounded-lg">
                                    <FaBell />
                                    <span>Requested by {syncRequest.requestedByName || "Employee"}</span>
                                </div>
                            )}
                            <button
                                onClick={() => handleGitHubSync(false)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition shadow-lg ${syncRequest
                                    ? "bg-yellow-500/20 border-yellow-500 text-yellow-500 hover:bg-yellow-500/30"
                                    : "bg-neutral-800 hover:bg-neutral-700 text-white border-neutral-700"
                                    }`}
                                title="Scan GitHub repo for new/deleted screens"
                            >
                                <FaSync className={loading ? "animate-spin" : ""} />
                                {loading ? "Syncing..." : (syncRequest ? "Approve & Sync" : "Sync Repo")}
                            </button>

                            {/* NUCLEAR RESET OPTION */}
                            {/* <button
                                onClick={() => handleGitHubSync(true)}
                                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-red-500/30 bg-red-500/10 text-red-500 hover:bg-red-500/20 transition shadow-lg"
                                title="Wipe everything and start fresh from GitHub"
                            >
                                <FaTimes className="text-red-400" />
                                Reset & Resync
                            </button> */}
                        </div>
                    ) : (
                        <button
                            onClick={handleRequestSync}
                            disabled={!!syncRequest}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition shadow-lg ${syncRequest
                                ? "bg-neutral-800/50 border-neutral-700 text-gray-500 cursor-not-allowed"
                                : "bg-neutral-800 hover:bg-neutral-700 text-white border-neutral-700"
                                }`}
                        >
                            {syncRequest ? (
                                <><FaCheck className="text-green-500" /> Request Pending</>
                            ) : (
                                <><FaSync className="text-gray-400" /> Request Update</>
                            )}
                        </button>
                    )}

                    {/* Access Control Button - HR ONLY */}
                    {user?.role === 'hr' && (
                        <button
                            onClick={() => setIsAccessModalOpen(true)}
                            className="flex items-center gap-2 bg-neutral-800 hover:bg-neutral-700 text-white px-4 py-2 rounded-xl border border-neutral-700 transition shadow-lg"
                        >
                            <FaUserPlus className="text-blue-400" /> Share Access
                        </button>
                    )}
                </div>
            </div>

            {/* Detailed Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {/* Total Screens */}
                <div className="bg-neutral-800/50 p-4 rounded-xl border border-neutral-700/50 backdrop-blur-sm relative overflow-hidden group">
                    <div className="relative z-10">
                        <div className="text-gray-400 text-xs uppercase tracking-wider font-semibold">Total Screens</div>
                        <div className="text-3xl font-bold text-white mt-1">{total}</div>
                    </div>
                    <FaLaptopCode className="absolute bottom-2 right-2 text-neutral-700/50 text-4xl group-hover:scale-110 transition-transform" />
                </div>

                {/* Launch Readiness */}
                <div className="bg-neutral-800/50 p-4 rounded-xl border border-neutral-700/50 backdrop-blur-sm relative overflow-hidden group">
                    <div className="relative z-10">
                        <div className="text-gray-400 text-xs uppercase tracking-wider font-semibold">Launch Ready</div>
                        <div className="flex items-end gap-2 mt-1">
                            <div className="text-3xl font-bold text-white">{progressPercent}%</div>
                            <div className="text-sm text-gray-500 mb-1">({fullyDone}/{total})</div>
                        </div>
                        <div className="w-full h-1 bg-neutral-700 rounded-full mt-2 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400" style={{ width: `${progressPercent}%` }} />
                        </div>
                    </div>
                </div>

                {/* Active Blockers */}
                {(() => {
                    const blockerCount = data.filter(i => i.blocker && i.blocker.trim().length > 0).length;
                    return (
                        <div className={`p-4 rounded-xl border backdrop-blur-sm relative overflow-hidden group transition-colors ${blockerCount > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-neutral-800/50 border-neutral-700/50'
                            }`}>
                            <div className="relative z-10">
                                <div className={`text-xs uppercase tracking-wider font-semibold ${blockerCount > 0 ? 'text-red-400' : 'text-gray-400'}`}>Active Blockers</div>
                                <div className={`text-3xl font-bold mt-1 ${blockerCount > 0 ? 'text-red-500' : 'text-white'}`}>{blockerCount}</div>
                            </div>
                            <FaExclamationTriangle className={`absolute bottom-2 right-2 text-4xl group-hover:scale-110 transition-transform ${blockerCount > 0 ? 'text-red-500/20' : 'text-neutral-700/50'}`} />
                        </div>
                    );
                })()}

                {/* Test Coverage */}
                {(() => {
                    const testDoneCount = data.filter(i => i.testCasesDone).length;
                    return (
                        <div className="bg-neutral-800/50 p-4 rounded-xl border border-neutral-700/50 backdrop-blur-sm relative overflow-hidden group">
                            <div className="relative z-10">
                                <div className="text-gray-400 text-xs uppercase tracking-wider font-semibold">QA Verified</div>
                                <div className="text-3xl font-bold text-white mt-1">{testDoneCount}</div>
                            </div>
                            <FaCheck className="absolute bottom-2 right-2 text-green-500/20 text-4xl group-hover:scale-110 transition-transform" />
                        </div>
                    );
                })()}
            </div>

            {/* Filters & Search */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <input
                        type="text"
                        placeholder="Search screens..."
                        className="w-full pl-10 pr-4 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-gray-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="relative">
                    <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <select
                        className="pl-10 pr-8 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-gray-200 focus:outline-none focus:border-blue-500 appearance-none cursor-pointer"
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                    >
                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                </div>
            </div>

            {/* Main Table */}
            <div className="overflow-x-auto rounded-xl border border-neutral-700/50 shadow-xl bg-neutral-800/20 backdrop-blur-md">
                <table className="w-full text-left border-collapse min-w-[1200px]">
                    <thead>
                        <tr className="bg-neutral-800/80 text-gray-400 text-xs uppercase tracking-wider border-b border-neutral-700">
                            <th className="p-4 font-semibold w-64">Screen Name</th>
                            <th className="p-4 font-semibold w-40">FE Status</th>
                            <th className="p-4 font-semibold w-40">BE Status</th>
                            <th className="p-4 font-semibold w-64">API Details</th>
                            <th className="p-4 font-semibold w-56">Blockers</th>
                            <th className="p-4 font-semibold w-64">Testing</th>
                            <th className="p-4 font-semibold w-64">BE Docs & Notes</th>
                            <th className="p-4 font-semibold w-40">Assets & Info</th>
                            <th className="p-4 font-semibold w-24">Last Edit</th>
                            {user?.role === 'hr' && <th className="p-4 font-semibold w-16 text-center">Delete</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-700/50 text-sm">
                        {filteredData.map((row) => (
                            <tr key={row.id} className="hover:bg-neutral-800/40 transition-colors group">

                                {/* Screen Name */}
                                <td className="p-4 align-top">
                                    <div className="font-semibold text-white text-base">{row.screen}</div>
                                    {row.category && <span className="text-xs text-gray-500 bg-neutral-800 px-1.5 py-0.5 rounded mt-1 inline-block">{row.category}</span>}

                                    {/* Code Link Input */}
                                    <div className="mt-2 flex items-center gap-2">
                                        {row.githubLink ? (
                                            <a
                                                href={row.githubLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Open in GitHub"
                                                className="hover:scale-110 transition-transform"
                                            >
                                                <FaGithub className="text-blue-400 hover:text-white cursor-pointer text-base" />
                                            </a>
                                        ) : (
                                            <FaGithub className="text-gray-500" />
                                        )}
                                    </div>
                                </td>

                                {/* FE Status */}
                                <td className="p-4 align-top">
                                    <select
                                        className={`w-full p-2 rounded-lg border bg-neutral-900 appearance-none cursor-pointer text-xs font-semibold ${FE_STATUS_OPTIONS.find(o => o.value === row.feStatus)?.color || 'text-gray-400 border-neutral-700'
                                            }`}
                                        value={row.feStatus || 'v1-red'}
                                        onChange={(e) => handleUpdate(row.id, 'feStatus', e.target.value)}
                                    >
                                        {FE_STATUS_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </td>

                                {/* BE Status */}
                                <td className="p-4 align-top">
                                    <select
                                        className={`w-full p-2 rounded-lg border bg-neutral-900 appearance-none cursor-pointer text-xs font-semibold ${BE_STATUS_OPTIONS.find(o => o.value === row.beStatus)?.color || 'text-gray-400 border-neutral-700'
                                            }`}
                                        value={row.beStatus || 'not-started'}
                                        onChange={(e) => handleUpdate(row.id, 'beStatus', e.target.value)}
                                    >
                                        {BE_STATUS_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </td>

                                {/* API Details */}
                                <td className="p-4 align-top">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs text-gray-400">API Ready?</span>
                                        <button
                                            onClick={() => handleUpdate(row.id, 'apiReady', !row.apiReady)}
                                            className={`w-10 h-5 rounded-full flex items-center transition-colors px-1 ${row.apiReady ? 'bg-green-500' : 'bg-neutral-600'
                                                }`}
                                        >
                                            <div className={`w-3 h-3 bg-white rounded-full transition-transform transform ${row.apiReady ? 'translate-x-5' : 'translate-x-0'
                                                }`} />
                                        </button>
                                    </div>
                                    <textarea
                                        className="w-full bg-neutral-800 border border-neutral-700 rounded-lg p-2 text-xs text-gray-300 focus:border-blue-500 resize-y min-h-[60px]"
                                        placeholder="List APIs used..."
                                        value={row.apisUsed || ''}
                                        onChange={(e) => handleUpdate(row.id, 'apisUsed', e.target.value)}
                                    />
                                </td>

                                {/* Blockers */}
                                <td className="p-4 align-top">
                                    <textarea
                                        className={`w-full bg-neutral-800 border rounded-lg p-2 text-xs focus:border-red-500 resize-y min-h-[60px] ${row.blocker ? 'border-red-500/50 text-red-300' : 'border-neutral-700 text-gray-300'
                                            }`}
                                        placeholder="Describe blockers..."
                                        value={row.blocker || ''}
                                        onChange={(e) => handleUpdate(row.id, 'blocker', e.target.value)}
                                    />
                                </td>

                                {/* Testing */}
                                <td className="p-4 align-top space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-400">Test Cases:</span>
                                        <input
                                            type="number"
                                            className="w-16 bg-neutral-800 border border-neutral-700 rounded p-1 text-xs text-center"
                                            value={row.testCasesCount || 0}
                                            onChange={(e) => handleUpdate(row.id, 'testCasesCount', parseInt(e.target.value))}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-400">All Done?</span>
                                        <button
                                            onClick={() => handleUpdate(row.id, 'testCasesDone', !row.testCasesDone)}
                                            className={`w-8 h-4 rounded-full flex items-center transition-colors px-0.5 ${row.testCasesDone ? 'bg-green-500' : 'bg-neutral-600'
                                                }`}
                                        >
                                            <div className={`w-3 h-3 bg-white rounded-full transition-transform transform ${row.testCasesDone ? 'translate-x-4' : 'translate-x-0'
                                                }`} />
                                        </button>
                                    </div>

                                    <textarea
                                        className="w-full bg-neutral-800 border border-neutral-700 rounded-lg p-2 text-xs text-gray-300 focus:border-blue-500 resize-y min-h-[40px]"
                                        placeholder="Paste test cases..."
                                        value={row.testCasesList || ''}
                                        onChange={(e) => handleUpdate(row.id, 'testCasesList', e.target.value)}
                                    />
                                </td>

                                {/* BE Docs & Notes */}
                                <td className="p-4 align-top space-y-2">
                                    <div className="flex flex-col gap-2">
                                        <textarea
                                            className="w-full bg-neutral-800 border border-neutral-700 rounded-lg p-2 text-xs text-gray-300 focus:border-green-500 resize-y min-h-[60px]"
                                            placeholder="Backend notes/logic..."
                                            value={row.beNotes || ''}
                                            onChange={(e) => handleUpdate(row.id, 'beNotes', e.target.value)}
                                        />

                                        <div className="flex items-center gap-2">
                                            <label className="flex-1 cursor-pointer bg-neutral-700/50 hover:bg-neutral-700 border border-neutral-600 rounded-lg px-2 py-1.5 transition flex items-center gap-2 text-[10px] font-medium text-gray-300">
                                                <FaFileUpload className="text-green-400" />
                                                <span>{row.beFile ? 'Change Doc' : 'Upload Doc'}</span>
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    onChange={(e) => handleBeFileUpload(row.id, e.target.files[0])}
                                                />
                                            </label>

                                            {row.beFile && (
                                                <a
                                                    href={row.beFile.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="p-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition shadow-sm"
                                                    title={`Download ${row.beFile.name}`}
                                                >
                                                    <FaFileExport />
                                                </a>
                                            )}
                                        </div>
                                        {row.beFile && (
                                            <p className="text-[9px] text-gray-500 truncate max-w-[150px]" title={row.beFile.name}>
                                                📎 {row.beFile.name}
                                            </p>
                                        )}
                                    </div>
                                </td>

                                {/* Assets (Screenshot) */}
                                <td className="p-4 align-top text-center">
                                    {row.screenshotUrl ? (
                                        <div className="relative group w-full h-20 bg-neutral-800 rounded-lg overflow-hidden border border-neutral-700">
                                            <img src={row.screenshotUrl} alt="Screen" className="w-full h-full object-cover" />
                                            <a
                                                href={row.screenshotUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-bold"
                                            >
                                                View
                                            </a>
                                        </div>
                                    ) : (
                                        <label className="cursor-pointer flex flex-col items-center justify-center w-full h-20 border border-dashed border-neutral-600 rounded-lg hover:bg-neutral-800 transition">
                                            <FaCamera className="text-gray-500 mb-1" />
                                            <span className="text-[10px] text-gray-500">Upload Screen</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={(e) => handleImageUpload(row.id, e.target.files[0])}
                                            />
                                        </label>
                                    )}
                                </td>

                                {/* Last Edited By */}
                                <td className="p-4 align-top text-center">
                                    {row.lastEditedBy ? (
                                        <div className="flex flex-col items-center">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-purple-500 to-pink-500 flex items-center justify-center text-xs font-bold text-white mb-1 shadow-md">
                                                {row.lastEditedBy.name.charAt(0)}
                                            </div>
                                            <span className="text-[10px] text-gray-400 leading-tight block max-w-[80px] truncate">{row.lastEditedBy.name}</span>
                                            <span className="text-[9px] text-gray-500">{new Date(row.updatedAt?.seconds * 1000).toLocaleDateString()}</span>
                                        </div>
                                    ) : (
                                        <span className="text-gray-600 text-xs">-</span>
                                    )}
                                </td>

                                {/* Delete Row (HR Only) */}
                                {user?.role === 'hr' && (
                                    <td className="p-4 align-top text-center">
                                        <button
                                            onClick={() => handleDeleteRow(row.id, row.screen)}
                                            className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                                            title="Delete Row"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {filteredData.length === 0 && (
                            <tr>
                                <td colSpan={user?.role === 'hr' ? "10" : "9"} className="text-center p-8 text-gray-500">
                                    <div className="flex flex-col items-center gap-2">
                                        <FaSearch className="text-3xl opacity-20" />
                                        <p>No screens found matching your criteria.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <ManageAccessModal
                isOpen={isAccessModalOpen}
                onClose={() => setIsAccessModalOpen(false)}
                appName={title}
                appId={appId}
            />
        </div>
    );
};

export default TechTrackingTable;
